<script setup lang="ts">
import { useCustomerReviewStore } from "@/store/modules/customerReview";
import StepwiseProgress from "../StepwiseProgress.vue";
import TripCard from "../TripCard.vue";
import TripCustomerFeedback2Field from "./TripCustomerFeedback2Field.vue";
import CUItinerarySkeletonLoader from "@/components/CUItinerarySkeletonLoader.vue";
import NotFoundLayout from "@/layouts/NotFoundLayout.vue";
import thanksGraphic from "@/assets/images/tripCustomerReview/thanksGraphic.svg";
import googleGraphic from "@/assets/images/tripCustomerReview/googleRating.png";
import i18n from "@/plugins/i18n";

import { computed, ref, onMounted, onBeforeUnmount, watch } from "vue";

const props = defineProps({
  reservationId: {
    type: String,
    required: true,
  },
  customerId: {
    type: String,
    required: true,
  },
  initialStars: {
    type: Number,
    default: null,
  },
});

const redirectTimer = ref<number | null>(null);
const GOOGLE_REVIEW_URL = 'https://search.google.com/local/writereview?placeid=ChIJe0x_h2MP9YgREF3VOKTU5W0';

const dataStore = useCustomerReviewStore();

onMounted(async () => {
  await dataStore.fetchReviewDetails(props.reservationId, props.customerId);
  if (props.initialStars) {
    dataStore.setInitialRating(props.initialStars);
  }
});

const progressSteps = ref<Array<{ title: string }>>([
  { title: i18n.t("reviewsV2.STEP_OVERALL").toString() },
  { title: i18n.t("reviewsV2.STEP_DRIVER").toString() },
  { title: i18n.t("reviewsV2.STEP_VEHICLE").toString() },
]);

const driverImprovementOptions = [
  {
    text: i18n.t("reviewsV2.DRIVER_IMPROVEMENT_OPTIONS.TIMELINESS").toString(),
    value: "TIMELINESS",
  },
  {
    text: i18n.t("reviewsV2.DRIVER_IMPROVEMENT_OPTIONS.PROFESSIONALISM").toString(),
    value: "PROFESSIONALISM",
  },
  {
    text: i18n.t("reviewsV2.DRIVER_IMPROVEMENT_OPTIONS.SAFETY").toString(),
    value: "SAFETY",
  },
  {
    text: i18n.t("reviewsV2.DRIVER_IMPROVEMENT_OPTIONS.ROUTE_CHOICE").toString(),
    value: "ROUTE_CHOICE",
  },
];

const vehicleImprovementOptions = [
  {
    text: i18n.t("reviewsV2.VEHICLE_IMPROVEMENT_OPTIONS.CONDITION").toString(),
    value: "CONDITION"
  },
  {
    text: i18n.t("reviewsV2.VEHICLE_IMPROVEMENT_OPTIONS.CLEANLINESS").toString(),
    value: "CLEANLINESS"
  },
  {
    text: i18n.t("reviewsV2.VEHICLE_IMPROVEMENT_OPTIONS.AMENITIES").toString(),
    value: "AMENITIES"
  }
];

const currentStepIdx = computed(() => dataStore.currentStepIdx);

const goNext = async () => {
  if (canGoNext) {
    dataStore.updateReview(props.reservationId, props.customerId);
  }
};

const goPrev = () => {
  if (canGoBack) {
    dataStore.updateCurrentStepIdx(currentStepIdx.value - 1);
  }
};

const nextText = computed(() => {
  switch (currentStepIdx.value) {
    case 0:
      return i18n.t("reviewsV2.STEP_OVERALL_BUTTON");
    case 1:
      return i18n.t("reviewsV2.STEP_DRIVER_BUTTON");
    case 2:
      return i18n.t("reviewsV2.STEP_VEHICLE_BUTTON");
    default:
      return "Next";
  }
});

const canGoBack = computed(() => {
  return currentStepIdx.value > 0;
});

const canGoNext = computed(() => {
  switch (currentStepIdx.value) {
    case 0:
      return dataStore.overallRating > 0;
    case 1:
      return dataStore.driverReviews.every((dr) => dr?.rating > 0) || (dataStore.useOverallDriverReview && dataStore.overallDriverRating > 0);
    case 2:
      return dataStore.vehicleReviews.every((vr) => vr?.rating > 0) || (dataStore.useOverallVehicleReview && dataStore.overallVehicleRating > 0);
    default:
      return false;
  }
});

watch(
  () => currentStepIdx.value > 3 && dataStore.userShouldRedirectToGoogle,
  (shouldRedirect) => {
    if (shouldRedirect) {
      if (redirectTimer.value) {
        clearTimeout(redirectTimer.value);
      }
      redirectTimer.value = window.setTimeout(() => {
        window.location.href = GOOGLE_REVIEW_URL;
      }, 4000);
    }
  }
);

onBeforeUnmount(() => {
  if (redirectTimer.value) {
    clearTimeout(redirectTimer.value);
  }
});

const handleUpdate = (item: string, value) => {
  if (item === "overall") {
    dataStore.overallRating = value.rating;
    if (value.rating === 5) {
      dataStore.driverReviews.forEach((review) => {
        review.rating = 5;
        review.driverImprovementAttributes = [];
        review.comment = "";
      });
      dataStore.vehicleReviews.forEach((review) => {
        review.rating = 5;
        review.vehicleImprovementAttributes = [];
        review.comment = "";
      });
    }
  } else if (item === "vehicle_overall") {
    dataStore.overallVehicleRating = value.rating;
    dataStore.overallVehicleComment = value.feedback;
    dataStore.overallVehicleImprovementAttributes = value.checkboxes;
  } else if (item === "driver_overall") {
    dataStore.overallDriverRating = value.rating;
    dataStore.overallDriverComment = value.feedback;
    dataStore.overallDriverImprovementAttributes = value.checkboxes;
  } else if (item.includes("driver")) {
    const driverIndex = parseInt(item.split("_")[1]);
    if (driverIndex !== -1) {
      dataStore.driverReviews[driverIndex].rating = value.rating;
      dataStore.driverReviews[driverIndex].comment = value.feedback;
      dataStore.driverReviews[driverIndex].driverImprovementAttributes = value.checkboxes;
    }
  } else if (item.includes("vehicle")) {
    const vehicleIndex = parseInt(item.split("_")[1]);
    if (vehicleIndex !== -1) {
      dataStore.vehicleReviews[vehicleIndex].rating = value.rating;
      dataStore.vehicleReviews[vehicleIndex].comment = value.feedback;
      dataStore.vehicleReviews[vehicleIndex].vehicleImprovementAttributes = value.checkboxes;
    }
  }
};

const handleBackClick = () => {
  goPrev();
};

const handleNextClick = () => {
  goNext();
};

</script>

<template>
  <div>
    <NotFoundLayout v-if="dataStore.notFound" :img="require('@/assets/images/oops@2x.png')">
      <template #content>
        This review link either is invalid or has expired.
      </template>
    </NotFoundLayout>
    <CUItinerarySkeletonLoader v-else-if="dataStore.loading" />
    <div v-else class="margin-b-16">
      <div v-if="currentStepIdx < 3">
        <StepwiseProgress
          :class="$vuetify.breakpoint.smAndDown ? 'margin-b-6' : 'margin-b-12'"
          :steps="progressSteps"
          :current-step-idx="currentStepIdx"
        />
        <TripCard
          :class="$vuetify.breakpoint.smAndDown ? 'margin-b-6' : 'margin-b-12'"
          :label="$t('reviewsV2.TRIP_CARD_LABEL').toString()"
          :trip-name="dataStore.tripDetails.tripName"
          :reservation-managed-id="dataStore.tripDetails.reservationManagedId"
          :drivers-count="dataStore.tripDetails.drivers.length"
          :vehicles-count="dataStore.tripDetails.vehicles.length"
          :scheduled-duration="dataStore.tripDetails.scheduledDurationInMinutes"
          :trip-date="dataStore.tripDetails.tripDate"
        />
        <form @submit.prevent>
          <TripCustomerFeedback2Field
            v-if="currentStepIdx === 0"
            :mainLabel="$t('reviewsV2.OVERALL_RATING_LABEL').toString()"
            name="overall"
            :include-feedback-text="false"
            :rating="dataStore.overallRating"
            @update="
              (evt) => {
                handleUpdate('overall', evt);
              }
            "
          />

          <div
            v-if="currentStepIdx === 1 && !dataStore.useOverallDriverReview"
            v-for="(driverReview, idx) in dataStore.driverReviews"
            :key="driverReview.driverName"
          >
            <TripCustomerFeedback2Field
              :class="
                $vuetify.breakpoint.smAndDown ? 'margin-b-6' : 'margin-b-12'
              "
              :main-label="
                $t('reviewsV2.DRIVER_RATING_LABEL', {
                  driverName: driverReview.driverName,
                }).toString()
              "
              :rating="driverReview.rating"
              :improvement-options="driverImprovementOptions"
              name="driver"
              :selected-improvements="driverReview.driverImprovementAttributes"
              :comment="driverReview.comment"
              @update="
                (evt) => {
                  handleUpdate(`driver_${idx}`, evt);
                }
              "
            />
          </div>
          <div
            v-if="currentStepIdx === 1 && dataStore.useOverallDriverReview"
          >
            <TripCustomerFeedback2Field
              :class="
                $vuetify.breakpoint.smAndDown ? 'margin-b-6' : 'margin-b-12'
              "
              :main-label="
                $t('reviewsV2.DRIVER_RATING_LABEL_OVERALL').toString()
              "
              :rating="dataStore.overallDriverRating"
              :improvement-options="driverImprovementOptions"
              name="driver"
              :selected-improvements="dataStore.overallDriverImprovementAttributes"
              :comment="dataStore.overallDriverComment"
              @update="
                (evt) => {
                  handleUpdate(`driver_overall`, evt);
                }
              "
            />
          </div>

          <div
            v-if="currentStepIdx === 2 && !dataStore.useOverallVehicleReview"
            v-for="(vehicleReview, idx) in dataStore.vehicleReviews"
            :key="vehicleReview.vehicleName + '_' + idx"
          >
            <TripCustomerFeedback2Field
              :class="
                $vuetify.breakpoint.smAndDown ? 'margin-b-6' : 'margin-b-12'
              "
              :rating="vehicleReview.rating"
              :main-label="
                $t('reviewsV2.VEHICLE_RATING_LABEL', {
                  vehicleType: vehicleReview.vehicleTypeLabel,
                  licensePlate: vehicleReview.licensePlate,
                }).toString()
              "
              :improvement-options="vehicleImprovementOptions"
              :selected-improvements="vehicleReview.vehicleImprovementAttributes"
              :comment="vehicleReview.comment"
              name="vehicle"
              @update="
                (evt) => {
                  handleUpdate(`vehicle_${idx}`, evt);
                }
              "
            />
          </div>
          <div
            v-if="currentStepIdx === 2 && dataStore.useOverallVehicleReview"
          >
            <TripCustomerFeedback2Field
              :class="
                $vuetify.breakpoint.smAndDown ? 'margin-b-6' : 'margin-b-12'
              "
              :rating="dataStore.overallVehicleRating"
              :mainLabel="
                $t('reviewsV2.VEHICLE_RATING_LABEL_OVERALL').toString()
              "
              :improvement-options="vehicleImprovementOptions"
              :selected-improvements="dataStore.overallVehicleImprovementAttributes"
              :comment="dataStore.overallVehicleComment"
              name="vehicle"
              @update="
                (evt) => {
                  handleUpdate(`vehicle_overall`, evt);
                }
              "
            />
          </div>
          <div
            class="display-flex justify-space-between"
            :class="$vuetify.breakpoint.smAndDown ? 'margin-t-6' : 'margin-t-12'"
          >
            <span>
              <button v-if="canGoBack" class="font-12" @click="handleBackClick">
                Back
              </button>
            </span>

            <v-btn
              small
              depressed
              color="primary"
              :disabled="!canGoNext"
              :loading="dataStore.updating"
              @click="handleNextClick"
            >
              {{ nextText }}
            </v-btn>
          </div>
        </form>
      </div>
      <div v-else-if="currentStepIdx === 3">
        <h1>{{ $t("reviewsV2.THANKS_TITLE") }}</h1>
        <p>{{ $t("reviewsV2.THANKS_SUBTITLE") }}</p>
        <img
          :src="thanksGraphic"
          alt="Thanks!"
          class="w-full margin-b-16 margin-t-16"
        />
      </div>
      <div v-else-if="currentStepIdx > 3">
        <h1>{{ $t("reviewsV2.REDIRECT_TITLE") }}</h1>
        <p>{{ $t("reviewsV2.REDIRECT_SUBTITLE") }}</p>
        <img :src="googleGraphic" alt="Thanks!" class="w-300" />
      </div>
    </div>
  </div>
</template>

<style scoped></style>
